import { useMutation, useQueryClient } from "@tanstack/react-query";

import { usePreferenceValue } from "@/hooks/use-preference-value";
import { type PlainUserPreference, userPreferencesQueryKey, useSetPreference } from "@/queries/user-preferences-queries";

export const ENVIRONMENT_START_COUNT_KEY = "ENVIRONMENT_START_COUNT";
const LOCAL_STORAGE_PENDING_KEY = "ENVIRONMENT_START_COUNT_PENDING";
const MAX_COUNT = 10;

function parseCount(value: string | null | undefined): number {
    if (value === undefined || value === null) {
        return 0;
    }
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
}

function getPendingIncrements(): number {
    try {
        return parseCount(localStorage.getItem(LOCAL_STORAGE_PENDING_KEY));
    } catch {
        return 0;
    }
}

function setPendingIncrements(count: number) {
    try {
        if (count <= 0) {
            localStorage.removeItem(LOCAL_STORAGE_PENDING_KEY);
        } else {
            localStorage.setItem(LOCAL_STORAGE_PENDING_KEY, count.toString());
        }
    } catch {
        // localStorage may be unavailable (private browsing, quota exceeded)
    }
}

/**
 * Hook that returns a fire-and-forget callback to increment the environment start count.
 * Safe to call from mutation callbacks — re-renders do not trigger the increment
 * because mutation.mutate is only invoked explicitly from onSuccess/onSettled handlers.
 * Falls back to localStorage on API failure. Caps at MAX_COUNT.
 */
export function useIncrementEnvironmentStartCount(): () => void {
    const queryClient = useQueryClient();
    const setPreference = useSetPreference();

    // Note: `value` is read from the closure at call time, not at mutation
    // settlement. If two increments fire in quick succession both may read the
    // same stale value, losing one increment. Acceptable given the cap at 10
    // and fire-and-forget semantics — precision is not critical for onboarding
    // stage thresholds.
    const mutation = useMutation({
        mutationFn: async () => {
            const preference = queryClient.getQueryData<PlainUserPreference>(
                userPreferencesQueryKey.getByKey(ENVIRONMENT_START_COUNT_KEY),
            );
            const remoteCount = parseCount(preference?.value);
            if (remoteCount >= MAX_COUNT) {
                setPendingIncrements(0);
                console.debug(`[EnvironmentStartCount] Already at cap (${MAX_COUNT}), skipping write`);
                return;
            }

            const pending = getPendingIncrements();
            const newCount = Math.min(remoteCount + pending + 1, MAX_COUNT);
            await setPreference.mutateAsync({ key: ENVIRONMENT_START_COUNT_KEY, value: newCount.toString() });
            return { remoteCount, pending, newCount };
        },
        onSuccess: (result) => {
            if (result) {
                const { remoteCount, pending, newCount } = result;
                setPendingIncrements(0);
                console.debug(
                    `[EnvironmentStartCount] Incremented: ${remoteCount} → ${newCount}` +
                        (pending > 0 ? ` (flushed ${pending} pending)` : ""),
                );
            }
        },
        onError: (e) => {
            const newPending = getPendingIncrements() + 1;
            console.error("Failed to increment environment start count, falling back to localStorage", e);
            setPendingIncrements(newPending);
            console.debug(`[EnvironmentStartCount] Stored pending increment in localStorage (pending: ${newPending})`);
        },
    });

    return mutation.mutate;
}

/**
 * Returns the current environment start count from user preferences,
 * including any pending localStorage increments that haven't synced yet.
 */
export function useEnvironmentStartCount(): { count: number | undefined; isLoading: boolean } {
    const { value, isLoading } = usePreferenceValue(ENVIRONMENT_START_COUNT_KEY);

    let count: number | undefined;
    if (value !== undefined) {
        count = parseCount(value) + getPendingIncrements();
    } else if (!isLoading) {
        const pending = getPendingIncrements();
        count = pending > 0 ? pending : undefined;
    }

    return { count, isLoading };
}
